import { useState, useEffect, useCallback, useRef } from 'react';
import type { PriceHistoryResponse } from '@/types';
import { apiClient, ApiClientError } from '@/api/client';

interface UseStationHistoryResult {
  data: PriceHistoryResponse | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

// Simple in-memory cache
const historyCache = new Map<string, { data: PriceHistoryResponse; timestamp: number }>();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

export function useStationHistory(stationId: string | null): UseStationHistoryResult {
  const [data, setData] = useState<PriceHistoryResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchHistory = useCallback(async (id: string, skipCache = false) => {
    // Check cache first
    if (!skipCache) {
      const cached = historyCache.get(id);
      if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
        setData(cached.data);
        setError(null);
        return;
      }
    }

    // Cancel any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    setIsLoading(true);
    setError(null);

    try {
      const response = await apiClient.getStationHistory(id);
      
      // Update cache
      historyCache.set(id, { data: response, timestamp: Date.now() });
      
      setData(response);
    } catch (err) {
      // Ignore abort errors
      if (err instanceof Error && err.name === 'AbortError') {
        return;
      }
      
      if (err instanceof ApiClientError) {
        setError(err.message);
      } else {
        setError('Failed to fetch price history');
      }
      console.error('Error fetching station history:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Refetch function (bypasses cache)
  const refetch = useCallback(async () => {
    if (stationId) {
      await fetchHistory(stationId, true);
    }
  }, [stationId, fetchHistory]);

  // Fetch when stationId changes
  useEffect(() => {
    if (!stationId) {
      setData(null);
      setError(null);
      return;
    }

    void fetchHistory(stationId);

    // Cleanup on unmount or stationId change
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [stationId, fetchHistory]);

  return {
    data,
    isLoading,
    error,
    refetch,
  };
}

// Utility to clear cache (useful for testing)
export function clearHistoryCache(): void {
  historyCache.clear();
}
